import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, withRouter } from "react-router-dom";

import { Dialog } from "@ui5/webcomponents-react/lib/Dialog";
import { Button } from "@ui5/webcomponents-react/lib/Button";
import { ButtonDesign } from "@ui5/webcomponents-react/lib/ButtonDesign";
import { AnalyticalCardHeader } from "@ui5/webcomponents-react/lib/AnalyticalCardHeader";
import { ValueState } from "@ui5/webcomponents-react/lib/ValueState";
import { PageBackgroundDesign } from "@ui5/webcomponents-react/lib/PageBackgroundDesign";
import { Page } from "@ui5/webcomponents-react/lib/Page";
import MessageStripCompnt from "../../shared/components/UIElements/MessageStrip";
import { Title } from "@ui5/webcomponents-react/lib/Title";
import { TitleLevel } from "@ui5/webcomponents-react/lib/TitleLevel";

import "@ui5/webcomponents-icons/dist/icons/list.js";
import "@ui5/webcomponents-icons/dist/icons/accounting-document-verification.js";
import "@ui5/webcomponents-icons/dist/icons/time-account.js";
import axios from "axios";

import "./RecommendationInfo.css";

const RecommendationInfo = (props) => {
  const approvalId = useParams().approvalId;
  const [error, setError] = useState("");
  const [payTotal, setPayTotal] = useState(0);
  const [docsCounter, setDocsCounter] = useState(1);
  const [totalApproved, setTotalApproved] = useState(0);
  const [totalRejected, setTotalRejected] = useState(0);
  const [isPayrunApproved, setIsPayrunApproved] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [wizardName, setWizardName] = useState("");
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  const getRecommendationInfo = async () => {
    setError(null);
    await axios
      .get(`/api/payruns/info/` + approvalId + "/" + props.user, {
        headers: { Authorization: props.token },
      })
      .then( async function (response) {
        setIsPayrunApproved(response.data.isApproved);
        if (response.data.DocsCounter === null) {
          setDocsCounter(0);
        } else {
          setDocsCounter(response.data.DocsCounter);
        }
        setCompanyName(response.data.CompanyName);
        setWizardName(response.data.WizardName);
        setPayTotal(new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: response.data.LocCurr,
          minimumFractionDigits: 2
        }).format(parseFloat(response.data.TotalLoc)));
        if (response.data.TotalLocApproved)
          setTotalApproved(new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: response.data.LocCurr,
            minimumFractionDigits: 2
          }).format(parseFloat(response.data.TotalLocApproved)));
        if (response.data.TotalLocRejected)
          setTotalRejected(new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: response.data.LocCurr,
            minimumFractionDigits: 2
          }).format(parseFloat(response.data.TotalLocRejected)));
      })
      .catch(function (error) {
        if (error.response) {
          if (error.response.status === 401) {
            setError(error.response.data.message);
            props.handleLogout();
          } else {
            setError(error.response.data.message);
          }
        } else {
          setError(
            "Something went wrong. Please verify your Internet connection."
          );
        }
      });
  };

  const navBackButtonHandler = () => {
    props.history.goBack();
  };

  useEffect(() => {
    getRecommendationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dialogRef = useRef();

  const openConfirmationBox = useCallback(() => {
    dialogRef.current.open();
  }, [dialogRef]);

  const closeConfirmationBox = useCallback(() => {
    dialogRef.current.close();
  }, [dialogRef]);

  const onHeaderPress = () => {
    props.history.push(`/approvals/${approvalId}/suppliers`);
  };

  const onSubmitHandler = async () => {
    setDisableSubmitButton(true);
    setError(null);
    await axios
      .patch(
        `/api/payruns/docs/` + approvalId,
        {
          approvalUserCode: props.user,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: props.token,
          },
        }
      )
      .then(async function (response) {
        await axios
          .patch(
            `/api/payruns/header/` + approvalId,
            {
              approvalUserCode: props.user,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: props.token,
              },
            }
          )
          .then(async function (response) {
            await deleteDocsApprovalTempEntries();
            closeConfirmationBox();
            setDisableSubmitButton(false);
            props.history.push("/approvals");
          })
          .catch(function (error) {
            setDisableSubmitButton(false);
            if (error.response.status === 401) {
              setError(error.response.data.message);
              props.handleLogout();
            } else {
              closeConfirmationBox();
              setError(error.response.data.message);
            }
          });
      })
      .catch(function (error) {
        setDisableSubmitButton(false);
        if (error.response.status === 401) {
          setError(error.response.data.message);
          props.handleLogout();
        } else {
          closeConfirmationBox();
          setError(error.response.data.message);
        }
      });
  };

  const deleteDocsApprovalTempEntries = async () => {
    await axios
      .delete(`/api/payruns/docs/approval/` + approvalId, {
        headers: {
          "Content-Type": "application/json",
          Authorization: props.token,
        },
      })
      .then(function (response) {})
      .catch(function (error) {
        if (error.response.status === 401) {
          setError(error.response.data.message);
          props.handleLogout();
        } else {
          closeConfirmationBox();
          setError(error.response.data.message);
        }
      });
  };

  return (
    <Page
      title={"Approval: " + wizardName}
      showFooter={false}
      showHeader={true}
      showBackButton={true}
      backgroundDesign={PageBackgroundDesign.Transparent}
      onNavButtonPress={navBackButtonHandler}
    >
      <div className="container">
        <Title className="title" level={TitleLevel.H3} wrap={false}>
          {companyName}
        </Title>
        <AnalyticalCardHeader
          className="card"
          title={"Requested Payment Total"}
          value={payTotal}
          valueState={ValueState.None}
          showIndicator={false}
          onHeaderPress={onHeaderPress}
        />
        <AnalyticalCardHeader
          className="card"
          title={"Total Approved"}
          value={totalApproved}
          valueState={ValueState.Success}
          showIndicator={false}
          onHeaderPress={onHeaderPress}
        />
        <AnalyticalCardHeader
          className="card"
          title={"Total Rejected"}
          value={totalRejected}
          valueState={ValueState.Error}
          showIndicator={false}
          onHeaderPress={onHeaderPress}
        />
        <br />
        <br />
        {error && <MessageStripCompnt type="error" message={error} />}
        {!isPayrunApproved && (
          <section>
            {docsCounter !== 0 ? (
              <Button
                disabled={true}
                design={ButtonDesign.Positive}
                icon="time-account"
                onClick={openConfirmationBox}
              >
                Submit
              </Button>
            ) : (
              <Button
                design={ButtonDesign.Positive}
                icon="time-account"
                onClick={openConfirmationBox}
              >
                Submit
              </Button>
            )}
          </section>
        )}
        <Dialog
          ref={dialogRef}
          stretch={false}
          headerText={"Confirmation"}
          initialFocus={""}
        >
          <p>Do you wish to confirm the payment for the approved documents?</p>
          <Button
            className="dialog_button_cancel"
            design={ButtonDesign.Negative}
            icon="decline"
            onClick={closeConfirmationBox}
          >
            Cancel
          </Button>
          <Button
            className="dialog_button_submit"
            design={ButtonDesign.Positive}
            icon="accept"
            onClick={onSubmitHandler}
            disabled={disableSubmitButton}
          >
            Submit
          </Button>
        </Dialog>
      </div>
    </Page>
  );
};

export default withRouter(RecommendationInfo);
